/**
 * Quebras de linha do texto corrido (pt-BR), em toda a página: o texto do
 * tema e o que vem do admin (artigos, descrições, coleções), inclusive o que
 * chega depois (gaveta do carrinho, busca, filtros).
 *
 * - Palavra com hífen não quebra no hífen ("bem-estar", "anti-inflamatório",
 *   "30-60"). O Acordo Ortográfico (Base XX) manda repetir o hífen no começo
 *   da linha seguinte quando a quebra cai nele, e o navegador não faz isso.
 * - "R$" fica com o valor, e o número fica com a unidade ou a palavra
 *   seguinte ("3 g", "30 dias", "2 caramelos").
 * - "·", "–", "—" e o "-" entre espaços ("1654 - Campinas") não abrem
 *   linha: ficam com a palavra anterior.
 *
 * Cada trecho vai para um <e-keep> (white-space: nowrap em e-base.css): um
 * elemento próprio, que nenhum seletor de span do tema alcança. Quando o
 * texto é filho direto de um flex ou grid, o trecho inteiro vai antes para
 * um <e-flow>, o mesmo item único que o navegador já criava (sem ele, cada
 * pedaço viraria um item e o texto deixaria de quebrar entre eles).
 * O texto continua o mesmo para cópia, busca na página e leitor de tela, e
 * o HTML do servidor, que é o que os buscadores leem, não muda. Trecho com
 * mais de 24 caracteres fica livre, para não estourar coluna estreita. Só o
 * espaço comum e o não separável entram: quebra de linha do próprio texto
 * (white-space: pre-line) continua valendo.
 */

const MAX_LENGTH = 24;
const SKIP = [
  'script', 'style', 'noscript', 'template', 'textarea', 'option', 'select', 'title',
  'svg', 'math', 'code', 'pre', 'kbd', 'samp', '[contenteditable]',
  // Região viva: reescrever o texto logo depois do anúncio faria o leitor repetir.
  '[aria-live]', '[role="status"]', '[role="alert"]',
  'e-keep', '.e-nowrap', '.e-num', '.e-visually-hidden',
].join(', ');
const ITEM_CONTAINERS = /(^|-)(flex|grid)$/;

// Alternativas em ordem de prioridade; espaço é só o comum e o não separável.
// SEPARATOR: o "·", "–", "—" ou "-" logo depois do trecho vai junto com ele.
// DASH: o "-" só quando vem isolado (separador), nunca o de "e-mail".
const SPACE = String.raw`[  ]`;
const DASH = String.raw`(?:[·–—]|-(?=${SPACE}|$))`;
const SEPARATOR = String.raw`(?:${SPACE}${DASH})?`;
const NUMBER = String.raw`\d+(?:[.,]\d+)*(?:[-–]\d+(?:[.,]\d+)*)?`;
const UNIT = String.raw`(?:%|[\p{L}\p{N}]+(?:[-/][\p{L}\p{N}]+)*)`;
const PATTERN = new RegExp(
  [
    String.raw`R\$${SPACE}\d+(?:[.,]\d+)*${SEPARATOR}`, // "R$ 239,90"
    String.raw`${NUMBER}${SPACE}${UNIT}${SEPARATOR}`, // "3 g", "30-60 minutos", "3 mg/kg"
    String.raw`[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)+${SEPARATOR}`, // "bem-estar"
    String.raw`[^\s]+${SPACE}${DASH}`, // "Creatina ·", "1654 -"
  ].join('|'),
  'gu',
);

// O texto é item de flex ou grid? display: contents não gera caixa: sobe até quem gera.
function isLayoutItem(textNode) {
  let parent = textNode.parentElement;
  let display = getComputedStyle(parent).display;
  while (display === 'contents' && parent.parentElement) {
    parent = parent.parentElement;
    display = getComputedStyle(parent).display;
  }
  return ITEM_CONTAINERS.test(display.split(' ').pop());
}

function matchesIn(text) {
  return [...text.matchAll(PATTERN)].filter((match) => match[0].length <= MAX_LENGTH);
}

function keepTogether({ textNode, matches, layoutItem }) {
  const text = textNode.data;
  const pieces = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.index > cursor) pieces.push(text.slice(cursor, match.index));
    const keep = document.createElement('e-keep');
    keep.textContent = match[0];
    pieces.push(keep);
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) pieces.push(text.slice(cursor));
  if (layoutItem) {
    const flow = document.createElement('e-flow');
    flow.append(...pieces);
    textNode.replaceWith(flow);
  } else {
    textNode.replaceWith(...pieces);
  }
}

function textNodesIn(root) {
  if (root.nodeType === Node.TEXT_NODE) {
    return root.parentElement && !root.parentElement.closest(SKIP) ? [root] : [];
  }
  if (root.nodeType !== Node.ELEMENT_NODE || root.closest(SKIP)) return [];
  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (node.parentElement.closest(SKIP) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  while (walker.nextNode()) nodes.push(walker.currentNode);
  return nodes;
}

function process(roots) {
  // Coleta antes de mexer: trocar nós no meio do percurso confunde o walker.
  // Todas as leituras de estilo vêm antes da primeira troca: alternar leitura
  // e escrita obriga o navegador a recalcular o estilo a cada nó, o que vira
  // tarefa longa com CPU lenta.
  roots
    .flatMap(textNodesIn)
    .map((textNode) => ({ textNode, matches: matchesIn(textNode.data) }))
    .filter(({ matches }) => matches.length > 0)
    .map((entry) => ({ ...entry, layoutItem: isLayoutItem(entry.textNode) }))
    .forEach(keepTogether);
}

const observer = new MutationObserver((records) => {
  const added = records.flatMap((record) => [...record.addedNodes]).filter((node) => node.isConnected);
  observer.disconnect();
  process(added);
  // Os registros das próprias trocas não precisam de outra passada.
  observer.takeRecords();
  observer.observe(document.body, { childList: true, subtree: true });
});

process([document.body]);
observer.observe(document.body, { childList: true, subtree: true });
